import { Link, useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import NewsListCard from "@/components/feature/NewsListCard";
import EmptyState from "@/components/base/EmptyState";
import SectionHeading from "@/components/feature/SectionHeading";
import { usePageMeta } from "@/lib/seo";
import { getTopicById, getTopicMovies, getTopicNews, topics } from "@/lib/media";

export default function TopicPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const topic = getTopicById(id);

  usePageMeta({
    title: topic ? `${topic.title} - 影视专题 - 枫与铃` : "影视专题 - 枫与铃",
    description: topic ? topic.intro : "浏览枫与铃精选影视专题，影视推荐与相关资讯一次看完。",
    keywords: topic ? `${topic.title},影视专题,影视推荐,枫与铃` : "影视专题,枫与铃",
  });

  if (!topic) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="pt-[72px]">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8">
            <EmptyState
              icon="ri-bookmark-line"
              title="这个专题不存在"
              description="专题可能已经更新，去看看其他精选合集吧。"
              actionLabel="返回首页"
              onAction={() => navigate("/")}
            />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const topicMovies = getTopicMovies(topic);
  const topicNews = getTopicNews(topic);
  const otherTopics = topics.filter((item) => item.id !== topic.id);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-[72px]">
        <section className="relative h-[320px] w-full overflow-hidden bg-foreground-950 md:h-[400px]">
          <img
            src={topic.cover}
            alt={topic.title}
            title={`${topic.title} 影视专题`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/55 to-foreground-950/30" />
          <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-[1280px] px-4 pb-8 md:px-8 md:pb-10">
            <nav
              className="mb-4 flex flex-wrap items-center gap-2 text-xs text-background-50/75"
              aria-label="面包屑"
            >
              <Link to="/" className="cursor-pointer hover:text-background-50">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm" />
              </span>
              <span className="text-background-50">专题</span>
            </nav>
            <span className="rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-medium text-foreground-950">
              精选专题
            </span>
            <h1 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-4xl">
              {topic.title}
            </h1>
            <p className="mt-2 text-sm text-background-50/85 md:text-base">{topic.subtitle}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-background-50/70">
              <span>{topicMovies.length} 部影视作品</span>
              <span className="h-3 w-px bg-background-50/30" />
              <span>{topicNews.length} 篇相关资讯</span>
              <span className="h-3 w-px bg-background-50/30" />
              <span>由枫与铃编辑部整理</span>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <div className="grid gap-8 lg:grid-cols-[1.9fr_1fr] lg:gap-10">
            <div className="min-w-0">
              <div className="rounded-lg border border-background-200 bg-background-100/70 p-5 md:p-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950">专题介绍</h2>
                <p className="mt-2 text-sm leading-7 text-foreground-700">{topic.intro}</p>
              </div>

              <div className="mt-10">
                <SectionHeading
                  eyebrow="Watchlist"
                  title="专题影视推荐"
                  description="点击任一作品查看剧情简介、评分与相关推荐。"
                />
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5">
                  {topicMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <SectionHeading
                  eyebrow="Reading"
                  title="专题资讯"
                  description="与片单主题相关的预告、评论与采访。"
                  actionLabel="更多资讯"
                  actionTo="/news"
                />
                <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-2">
                  {topicNews.map((item) => (
                    <NewsListCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">其他专题</h2>
                <div className="mt-3 flex flex-col gap-4">
                  {otherTopics.map((item) => (
                    <Link
                      key={item.id}
                      to={`/topic/${item.id}`}
                      className="group flex cursor-pointer gap-3"
                    >
                      <div className="h-16 w-24 shrink-0 overflow-hidden rounded bg-background-200">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} 影视专题`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </p>
                        <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-foreground-500">
                          {item.subtitle}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">专题说明</h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  专题内容由编辑依据站内热度与题材相似度整理，每两周更新一次。站内不提供播放与下载，仅用于影视推荐与资讯展示。
                </p>
                <Link
                  to="/favorites"
                  className="mt-4 flex cursor-pointer items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                >
                  查看我的收藏
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-s-line text-sm" />
                  </span>
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}